import type { Ref } from "react";
import { useCallback } from "react";
import { useRouter } from "solito/router";
import type { TamaguiElement } from "tamagui";
import { Button, YStack } from "@medbillai/ui";
import { issueUrl } from "@medbillai/utils";
import { analyticsService } from "../../../lib/analytics";
import { useViewAllCasesAction } from "../../../provider/cases-panel";
import { CaseCard } from "./CaseCard";
import { outcomeForWin } from "./CaseSummary";
import { isActionRequired } from "./caseSignals";
import { HomeSection } from "./HomeSection";
import type { ClosedWin } from "./resolveClosedWin";
import type { YourCase } from "./useYourCases";
const SOURCE = "your-cases-home";

// Section-owned open action: one track-then-navigate for case rows and the
// demoted-win Review button; CaseCard stays presentational.
function useOpenCase() {
  const {
    push
  } = useRouter();
  return useCallback((target: {
    id: string;
    title?: string | null;
  }, source: string) => {
    analyticsService.trackEvent("OpenedIssue", {
      id: target.id,
      source
    });
    push(issueUrl({
      issueId: target.id,
      title: target.title
    }));
  }, [push]);
}
type YourCasesSectionProps = {
  cases: YourCase[];
  /** Total open cases; when it exceeds the visible list, "View all" shows. */
  totalCount: number;
  /** Demoted closed-wins (superseded) shown after the open cases. */
  demotedWins?: ClosedWin[];
  /** Renders skeleton rows through the real section layout. */
  loading?: boolean;
  /** Ref to the section root (the attention alert scrolls it into view). */
  containerRef?: Ref<TamaguiElement>;
};
const SKELETON_ROW_KEYS = ["row-0", "row-1", "row-2"] as const;

// A demoted win renders the Resolved CaseCard with an embedded result banner.
// Review opens the case; the rating is left there.
function DemotedWinCard({
  win
}: {
  win: ClosedWin;
}) {
  const openCase = useOpenCase();
  const handleReview = useCallback(() => {
    openCase({
      id: win.issueId,
      title: win.title
    }, "your_cases_home_resolved_review");
  }, [win.issueId, win.title, openCase]);
  return <CaseCard case={{
    id: win.issueId,
    title: win.title,
    actionRequired: false,
    resolvedWin: {
      outcome: outcomeForWin(win),
      savings: win.savings,
      onReview: handleReview
    }
  }} data-sentry-element="CaseCard" data-sentry-component="DemotedWinCard" data-sentry-source-file="YourCasesSection.tsx" />;
}

// Desktop opens the case-list panel; elsewhere it links to the cases page.
function ViewAllLink() {
  const {
    href,
    onPress
  } = useViewAllCasesAction(SOURCE);
  return <Button testID="your-cases.view-all" variant="tertiary" size="medium" fillWidth={false} href={href} onPress={onPress} data-sentry-element="Button" data-sentry-component="ViewAllLink" data-sentry-source-file="YourCasesSection.tsx">
      View all
    </Button>;
}
export function YourCasesSection({
  cases,
  totalCount,
  demotedWins = [],
  loading = false,
  containerRef
}: YourCasesSectionProps) {
  const openCase = useOpenCase();

  // Omit-empty: nothing to show means no section. Demoted wins count as content.
  if (!loading && cases.length === 0 && demotedWins.length === 0) {
    return null;
  }
  return <HomeSection title="Your cases" loading={loading} containerRef={containerRef} headerTrailing={totalCount > cases.length ? <ViewAllLink /> : undefined} data-sentry-element="HomeSection" data-sentry-component="YourCasesSection" data-sentry-source-file="YourCasesSection.tsx">
      {/* Tighter inter-card gap than the section's title→content gap. */}
      <YStack gap="$sp1" data-sentry-element="YStack" data-sentry-source-file="YourCasesSection.tsx">
        {loading ? SKELETON_ROW_KEYS.map(key => <CaseCard key={key} loading />) : <>
            {cases.map(caseItem => <CaseCard key={caseItem.id} onPress={() => openCase(caseItem, SOURCE)} case={{
          id: caseItem.id,
          title: caseItem.title,
          lastEventSentAt: caseItem.lastEvent?.sentAt,
          actionRequired: isActionRequired(caseItem)
        }} />)}
            {demotedWins.map(win => <DemotedWinCard key={win.issueId} win={win} />)}
          </>}
      </YStack>
    </HomeSection>;
}